import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  FileText, 
  Search, 
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Pen,
  Eye
} from 'lucide-react';
import api from '../../lib/api';

type ContractStatus = 'DRAFT' | 'PENDING_SIGNATURES' | 'PARTIALLY_SIGNED' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED';
type SignatureStatus = 'PENDING' | 'SIGNED' | 'DECLINED' | 'EXPIRED';

interface MyContract {
  id: string;
  verificationCode: string;
  status: ContractStatus;
  createdAt: string;
  expiresAt?: string;
  template?: {
    name: string;
    category: string;
  };
  mySignature?: {
    status: SignatureStatus;
    signedAt?: string;
  };
}

const STATUS_CONFIG: Record<ContractStatus, { icon: typeof CheckCircle; color: string; bgColor: string; label: string }> = {
  DRAFT: { icon: FileText, color: 'text-gray-600', bgColor: 'bg-gray-100', label: 'Rascunho' },
  PENDING_SIGNATURES: { icon: Clock, color: 'text-yellow-600', bgColor: 'bg-yellow-100', label: 'Aguardando' },
  PARTIALLY_SIGNED: { icon: AlertTriangle, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Parcial' },
  COMPLETED: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Concluído' },
  CANCELLED: { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100', label: 'Cancelado' },
  EXPIRED: { icon: AlertTriangle, color: 'text-gray-600', bgColor: 'bg-gray-100', label: 'Expirado' },
};

export function MyContracts() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'signed'>('all');

  const { data: contracts, isLoading } = useQuery({
    queryKey: ['my-contracts', search, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('signatureStatus', statusFilter);
      
      const response = await api.get<MyContract[]>(`/contracts/my?${params}`);
      return response.data;
    },
  });

  const pendingCount = contracts?.filter(c => c.mySignature?.status === 'PENDING').length || 0;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Meus Contratos</h1>
        <p className="text-gray-600">Visualize e assine seus contratos pendentes</p>
      </div>

      {/* Pending alert */}
      {pendingCount > 0 && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3">
          <Pen className="w-5 h-5 text-yellow-600" />
          <p className="text-yellow-800">
            Você tem <strong>{pendingCount}</strong> contrato(s) aguardando sua assinatura.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar contratos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'pending' | 'signed')}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Todos</option>
            <option value="pending">Pendentes</option>
            <option value="signed">Assinados</option>
          </select>
        </div>
      </div>

      {/* Contracts list */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-12 text-gray-500">Carregando...</div>
        ) : contracts?.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">Nenhum contrato encontrado</p>
          </div>
        ) : (
          contracts?.map((contract) => {
            const statusConfig = STATUS_CONFIG[contract.status];
            const StatusIcon = statusConfig.icon;
            const needsSignature = contract.mySignature?.status === 'PENDING';

            return (
              <div
                key={contract.id}
                className={`bg-white rounded-lg shadow p-4 border-l-4 ${
                  needsSignature ? 'border-l-yellow-500' : 'border-l-transparent'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg ${statusConfig.bgColor} flex items-center justify-center`}>
                      <StatusIcon className={`w-6 h-6 ${statusConfig.color}`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {contract.template?.name || 'Contrato'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        #{contract.verificationCode.slice(0, 8)} • {contract.template?.category}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Criado em {format(new Date(contract.createdAt), "dd/MM/yyyy", { locale: ptBR })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* My signature status */}
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Minha assinatura</p>
                      {contract.mySignature?.status === 'SIGNED' ? (
                        <span className="inline-flex items-center gap-1 text-sm text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          Assinado
                        </span>
                      ) : contract.mySignature?.status === 'PENDING' ? (
                        <span className="inline-flex items-center gap-1 text-sm text-yellow-600">
                          <Clock className="w-4 h-4" />
                          Pendente
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {needsSignature ? (
                        <Link
                          to={`/banking/contracts/${contract.id}/sign`}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          <Pen className="w-4 h-4" />
                          Assinar
                        </Link>
                      ) : (
                        <Link
                          to={`/banking/contracts/${contract.id}`}
                          className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
                        >
                          <Eye className="w-4 h-4" />
                          Ver
                        </Link>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expiration warning */}
                {contract.expiresAt && needsSignature && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-orange-600 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Expira em {format(new Date(contract.expiresAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </p>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default MyContracts;
